import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  CheckCircle2, Award, ShieldCheck, Mail, Building2, Sparkles, Clock
} from "lucide-react";

const veillePeriods = {
  mensuel: { price: "90€", period: "/mois", savings: null, id: "veille_mensuel" },
  trimestriel: { price: "270€", period: "/3 mois", priceMonthly: "90€/mois", savings: null, id: "veille_trimestriel" },
  semestriel: { price: "432€", period: "/6 mois", priceMonthly: "72€/mois", savings: "20%", id: "veille_semestriel" },
  annuel: { price: "756€", period: "/an", priceMonthly: "63€/mois", savings: "30%", id: "veille_annuel" }
};

/**
 * Pricing tier comparison block. Uses internal selector for the veille period.
 * Wired to navigate via the prop callback.
 */
export default function PricingSection({ onSubscribe }) {
  const [selectedVeillePeriod, setSelectedVeillePeriod] = useState("mensuel");
  const navigate = useNavigate();
  const currentVeillePlan = veillePeriods[selectedVeillePeriod];

  const pricingPlans = [
    {
      id: "free",
      name: "Essai Gratuit",
      price: "0€",
      period: "/15 jours",
      description: "Pour découvrir la plateforme",
      features: [
        "3 recommandations IA par semaine",
        "Accès aux marchés multi-sources",
        "Profil entreprise basique",
      ],
      highlighted: false,
    },
    {
      id: currentVeillePlan.id,
      name: "Veille Hebdo",
      price: currentVeillePlan.price,
      period: currentVeillePlan.period,
      priceMonthly: currentVeillePlan.priceMonthly,
      savings: currentVeillePlan.savings,
      description: "Veille personnalisée",
      features: [
        "Recommandations IA illimitées",
        "Alertes email personnalisées",
        "Toutes les sources (BOAMP, TED, SNCF...)",
        "Sauvegarde illimitée de favoris",
        "Sans engagement",
      ],
      highlighted: true,
      showPeriodSelector: true,
    },
    {
      id: "pack_memoires",
      name: "Pack Mémoires",
      price: "149€",
      period: "unique",
      description: "3 mémoires techniques",
      features: [
        "3 mémoires pré-rédigés par IA",
        "Adaptables à votre entreprise",
        "Prêts à déposer",
        "Support prioritaire",
      ],
      highlighted: false,
    },
  ];

  return (
    <>
            <section id="pricing" className="py-20 bg-gradient-to-br from-violet-900 via-indigo-900 to-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-display text-3xl md:text-5xl font-semibold text-white mb-4">
              Nos Packs & Options
            </h2>
            <p className="text-slate-300 text-lg max-w-2xl mx-auto">
              Pour booster vos réponses aux appels d&apos;offres
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {pricingPlans.map((plan, index) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={plan.highlighted ? "scale-105" : ""}
              >
                <Card 
                  className={`h-full ${plan.highlighted 
                    ? "bg-white ring-2 ring-violet-500 shadow-2xl shadow-violet-500/20" 
                    : "bg-white/5 backdrop-blur-sm border-white/10"}`}
                  data-testid={`pricing-card-${plan.id}`}
                >
                  <CardContent className="p-8">
                    {plan.highlighted && (
                      <div className="text-xs font-semibold text-violet-600 uppercase tracking-wider mb-4">
                        Plus populaire
                      </div>
                    )}
                    <h3 className={`font-display text-2xl font-bold mb-2 ${plan.highlighted ? "text-slate-900" : "text-white"}`}>
                      {plan.name}
                    </h3>
                    <p className={`text-sm mb-4 ${plan.highlighted ? "text-slate-600" : "text-slate-400"}`}>
                      {plan.description}
                    </p>
                    
                    {/* Period selector for Veille Hebdo */}
                    {plan.showPeriodSelector && (
                      <div className="flex gap-1 mb-4 bg-slate-100 p-1 rounded-lg">
                        {Object.entries(veillePeriods).map(([key, value]) => (
                          <button
                            key={key}
                            onClick={() => setSelectedVeillePeriod(key)}
                            className={`flex-1 py-1.5 px-2 text-xs font-medium rounded-md transition-all ${
                              selectedVeillePeriod === key 
                                ? "bg-violet-600 text-white shadow-sm" 
                                : "text-slate-600 hover:bg-slate-200"
                            }`}
                          >
                            {key === "mensuel" ? "1 mois" : key === "trimestriel" ? "3 mois" : key === "semestriel" ? "6 mois" : "1 an"}
                          </button>
                        ))}
                      </div>
                    )}
                    
                    {/* Savings badge */}
                    {plan.savings && (
                      <div className="mb-3">
                        <Badge className="bg-green-100 text-green-700 text-xs">
                          {plan.savings}
                        </Badge>
                      </div>
                    )}
                    
                    <div className="mb-2">
                      <span className={`text-4xl font-display font-bold ${plan.highlighted ? "text-slate-900" : "text-white"}`}>
                        {plan.price}
                      </span>
                      <span className={plan.highlighted ? "text-slate-600" : "text-slate-400"}>
                        {plan.period}
                      </span>
                    </div>
                    
                    {/* Monthly price for longer periods */}
                    {plan.priceMonthly && (
                      <p className={`text-sm mb-4 ${plan.highlighted ? "text-slate-500" : "text-slate-400"}`}>
                        soit {plan.priceMonthly}
                      </p>
                    )}
                    {!plan.priceMonthly && <div className="mb-4"></div>}
                    
                    <ul className="space-y-3 mb-8">
                      {plan.features.map((feature, i) => (
                        <li key={`item-${i}`} className={`flex items-start gap-3 ${plan.highlighted ? "text-slate-700" : "text-slate-300"}`}>
                          <CheckCircle2 className={`w-5 h-5 mt-0.5 flex-shrink-0 ${plan.highlighted ? "text-violet-600" : "text-violet-400"}`} />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button 
                      className={`w-full rounded-full py-6 ${plan.highlighted 
                        ? "bg-violet-700 hover:bg-violet-800 text-white" 
                        : "bg-white/10 hover:bg-white/20 text-white border border-white/20"}`}
                      onClick={() => plan.price === "0€" ? onSubscribe() : navigate("/pricing")}
                      data-testid={`pricing-btn-${plan.id}`}
                    >
                      {plan.price === "0€" ? "Essayer maintenant" : "Choisir ce plan"}
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
